import {
	QueryObserverOptions,
	useMutation,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query'
import { useAuth } from '../auth/useAuth'
import { DefaultGetParamsProps } from '../../lib/api/types/interfaces'
import { handleApiError } from '../../lib/utils/handleApiError'
import { CalendarService } from '../../lib/api/calendar.services'
import {
	GetCalendarEventsParamsProps,
	GetCalendarEventsResponseData,
	GetCalendarEventResponseData,
	UpdateCalendarEvent,
} from '../../lib/api/types/calendar.interfaces'

export const useEvents = (
	{ ...params }: GetCalendarEventsParamsProps,
	options?: Partial<QueryObserverOptions<GetCalendarEventsResponseData>>,
) => {
	const { isAuthenticated } = useAuth()
	return useQuery<GetCalendarEventsResponseData>({
		queryKey: ['events', params],
		queryFn: () => CalendarService.getEvents({ ...params }),
		enabled: isAuthenticated,
		staleTime: 1000 * 5,
		retry: 1,
		...options,
	})
}

export const useEvent = (
	{ id }: DefaultGetParamsProps,
	options?: Partial<QueryObserverOptions<GetCalendarEventResponseData>>,
) => {
	const { isAuthenticated } = useAuth()
	return useQuery<GetCalendarEventResponseData>({
		queryKey: ['event', id],
		queryFn: () => CalendarService.getEventById({ id }),
		enabled: isAuthenticated && Boolean(id),
		staleTime: 1000 * 5,
		retry: 1,
		...options,
	})
}

export const useCreateEvent = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['eventCreate'],
		mutationFn: ({ data }: { data: UpdateCalendarEvent }) =>
			CalendarService.createEvent({ data }),
		onError: (error, { data }) => handleApiError(error, data?.title),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['events'],
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useUpdateEvent = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['eventUpdate'],
		mutationFn: ({ data, id }: { data: UpdateCalendarEvent; id: string }) =>
			CalendarService.updateEvent({ data, id }),
		onError: (error) => handleApiError(error),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['events'],
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useDeleteEvent = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: ['eventDelete'],
		mutationFn: ({ id }: DefaultGetParamsProps) =>
			CalendarService.deleteEvent({ id }),
		onError: (error) => {
			console.error(error)
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['events'],
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}
